/**
 * useDebounce Hook
 *
 * Delays updating a value until the user stops changing it for a specified delay.
 * Useful for search inputs to prevent API calls on every keystroke.
 *
 * @param value - The value to debounce
 * @param delay - Delay in milliseconds
 * @returns Debounced value
 */

import { useState, useEffect } from 'react'

export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    // Set up timeout to update debounced value after delay
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    // Clean up timeout if value changes before delay completes
    // This prevents outdated values from being set
    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}
